import { useMutation, type UseMutationOptions } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { i18n } from '~/utils';

import { type ParsingCaseActionMutationResponse } from '../types';
import { useParsingCaseActionMutationOptions } from '../useParsingCaseActionMutationOptions';

import { retryParsingCase } from './retryParsingCase';

/**
 * Custom hook to retry a parsing case from start (full pipeline).
 * Uses the `useMutation` hook from `@tanstack/react-query` internally.
 *
 * @param options - Optional react-query mutation options
 * @returns The mutation result
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Parsing-Cases/operation/post_retry_case_vadmin_parsing_cases__case_id__actions_retry_post
 */
export const useMutationRetryParsingCase = (
  options?: UseMutationOptions<
    ParsingCaseActionMutationResponse,
    unknown,
    UUID
  >,
) => {
  const actionOptions = useParsingCaseActionMutationOptions<UUID>(() =>
    i18n.t(($) => $.retryParsingCase.success, { ns: 'data' }),
  );

  return useMutation<ParsingCaseActionMutationResponse, unknown, UUID>({
    mutationFn: async (caseId: UUID) => retryParsingCase(caseId),
    ...actionOptions,
    ...options,
  });
};
